import React, { useState } from 'react';
import { SPM9_CID_CATEGORIES, SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Grid, Sun, CloudRain, Anchor, Snowflake, Volume2, Sparkles } from 'lucide-react';

interface SPM9CIDMatrixProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

export const SPM9CIDMatrix: React.FC<SPM9CIDMatrixProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['spm9'];
  const audioEngine = AudioEngine.getInstance();

  const [activeDriverName, setActiveDriverName] = useState<string | null>(null);

  const handleTriggerCID = (driverName: string, soundType: string) => {
    setActiveDriverName(driverName);
    if (!audioEngine.getIsUnlocked()) return;

    audioEngine.stopAll();

    switch (soundType) {
      case 'warm_drone':
      case 'heat_burst':
        audioEngine.startAnthropoPulse(523.25, 7, 0.4); // C5 bright FM
        setTimeout(() => audioEngine.stopAnthropoPulse(), 1200);
        break;
      case 'crisp_hiss':
      case 'cryo_decay':
        audioEngine.startCryoHiss(9000, 0.3);
        setTimeout(() => audioEngine.stopCryoHiss(), 1200);
        break;
      case 'rain_rumble':
      case 'river_surge':
        audioEngine.startAerosolShield(300, 2, 0.35);
        setTimeout(() => audioEngine.stopAerosolShield(), 1200);
        break;
      case 'sub_ocean':
      case 'thaw_sub':
        audioEngine.startAbyssalDrone(35, 0.45);
        setTimeout(() => audioEngine.stopAbyssalDrone(), 1200);
        break;
      default:
        audioEngine.triggerExtremeTransient(600, 0, 0.6);
        break;
    }
  };

  return (
    <div className="space-y-6">
      {/* Title Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>
      </div>

      {/* Main Interactive Matrix Module */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
        <span className="text-xs font-mono text-slate-400 block font-semibold">
          Click any Climate Impact-Driver (CID) cell to trigger its procedural synth texture:
        </span>

        <div className="space-y-6">
          {SPM9_CID_CATEGORIES.map((cat) => (
            <div key={cat.category} className="space-y-3">
              <h3 className="text-xs font-bold font-mono text-cyan-300 uppercase tracking-wider border-b border-slate-800 pb-1.5 flex items-center gap-2">
                <Grid className="w-4 h-4 text-cyan-400" />
                <span>{cat.category}</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {cat.drivers.map((driver) => {
                  const isSelected = activeDriverName === driver.name;
                  return (
                    <div
                      key={driver.name}
                      onClick={() => handleTriggerCID(driver.name, driver.soundType)}
                      className={`p-4 rounded-2xl border cursor-pointer transition-all duration-200 glass-panel-hover flex items-center justify-between gap-3 ${
                        isSelected
                          ? 'bg-slate-800 border-cyan-400 glow-cyan shadow-md'
                          : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                      }`}
                    >
                      <div className="space-y-1">
                        <span className="text-xs font-bold text-white block">{driver.name}</span>
                        <span className="text-[10px] font-mono text-slate-400 block">
                          Trend: {driver.globalTrend}
                        </span>
                      </div>

                      <div className={`p-2 rounded-xl ${isSelected ? 'bg-cyan-500 text-slate-950' : 'bg-slate-800 text-slate-400'}`}>
                        <Volume2 className="w-4 h-4" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
